import apiClient from '../api/client';
import { RECAPTCHA_ENDPOINTS } from '../api/endpoints';
import type { Quiz } from '../types';

type RecaptchaAction = 'quiz_submit' | 'cij_submit';
interface EnterpriseRecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: RecaptchaAction }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: { enterprise?: EnterpriseRecaptcha };
  }
}

const TIMEOUT_MS = 10000;
let scriptPromise: Promise<void> | undefined;

function withTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Verification unavailable')), TIMEOUT_MS);
    promise.then((value) => { clearTimeout(timer); resolve(value); }, () => {
      clearTimeout(timer);
      reject(new Error('Verification unavailable'));
    });
  });
}

function loadScript(siteKey: string): Promise<void> {
  if (window.grecaptcha?.enterprise) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
  script.async = true;
  const loading = withTimeout(new Promise<void>((resolve, reject) => {
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Verification unavailable'));
    document.head.appendChild(script);
  })).then(() => {
    if (!window.grecaptcha?.enterprise) throw new Error('Verification unavailable');
  }).catch(() => {
    script.remove();
    scriptPromise = undefined;
    throw new Error('Verification unavailable');
  });
  scriptPromise = loading;
  return loading;
}

export function quizRequiresRecaptcha(quiz: Quiz | null): boolean {
  return Boolean(quiz?.questions.some((question) =>
    question.type === 'FormEntry' && question.metadata?.recaptcha?.enabled === true));
}

export function isRecaptchaFailure(error: unknown): boolean {
  const code = (error as { response?: { data?: { error?: { code?: unknown } } } } | null)?.response?.data?.error?.code;
  return typeof code === 'string' && code.startsWith('RECAPTCHA_');
}

export async function executeRecaptcha(action: RecaptchaAction): Promise<string> {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim();
  if (!siteKey) throw new Error('Verification unavailable');
  await loadScript(siteKey);
  const enterprise = window.grecaptcha?.enterprise;
  if (!enterprise) throw new Error('Verification unavailable');
  await withTimeout(new Promise<void>((resolve) => enterprise.ready(resolve)));
  const token = await withTimeout(Promise.resolve().then(() => enterprise.execute(siteKey, { action })));
  if (typeof token !== 'string' || !token.trim() || token.length > 8192) throw new Error('Verification unavailable');
  return token;
}

export async function verifyCijRecaptcha(): Promise<void> {
  const recaptchaToken = await executeRecaptcha('cij_submit');
  const response = await apiClient.post<{ verified?: boolean }>(
    RECAPTCHA_ENDPOINTS.VERIFY,
    { recaptchaToken },
    { headers: { 'X-Skip-Auth': 'true' } },
  );
  if (response.data?.verified !== true) throw new Error('Verification failed');
}